// src/services/surveyResponse.service.ts
import { db } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import {
  SurveyResponse,
  QuestionResponse,
  CreateSurveyResponseDto,
} from '../types/survey.types';
import { LocationCapture } from './location.service';
import { addressService } from './address.service';
import { workloadService } from './workload.service';

const COLLECTION_NAME = 'surveyResponses';


export async function createResponse(
  data: CreateSurveyResponseDto,
  userId: string,
  userName: string,
  location?: LocationCapture
): Promise<SurveyResponse> {
  const now = Timestamp.now();

  const responseData = {
    surveyId: data.surveyId,
    surveyVersion: data.surveyVersion,
    workloadId: data.workloadId,
    workloadName: data.workloadName || null,
    addressId: data.addressId,
    projectId: data.projectId,
    respondentId: userId,
    respondentName: userName,
    // Canvasser who submitted
    submittedBy: userId,
    submittedByName: userName,
    // Resident info (indexed for searching)
    residentName: data.residentName || null,
    residentPhone: data.residentPhone || null,
    residentEmail: data.residentEmail || null,
    addressStreet: data.addressStreet || null,
    // Response data
    responses: data.responses,
    status: data.discontinued ? 'discontinued' : 'completed' as const,
    discontinued: data.discontinued || false,
    startedAt: data.startedAt || now,
    completedAt: now,
    duration: data.startedAt
      ? now.toMillis() - (data.startedAt?.toMillis?.() || data.startedAt)
      : null,
    location: location
      ? {
          lat: location.lat,
          lng: location.lng,
          accuracy: location.accuracy,
          capturedAt: Timestamp.fromMillis(location.timestamp),
        }
      : null,
    deviceInfo: {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      platform: typeof navigator !== 'undefined' ? navigator.platform : null,
      language: typeof navigator !== 'undefined' ? navigator.language : null,
    },
    syncStatus: 'synced' as const,
    createdAt: now,
    updatedAt: now,
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), responseData);

  const response: SurveyResponse = {
    id: docRef.id,
    ...responseData,
  } as SurveyResponse;

  // Link response to address and update workload progress
  if (data.workloadId && data.addressId) {
    await addressService.linkSurveyResponse(
      data.workloadId,
      data.addressId,
      response.id
    );

    // Update address status to completed if survey was completed (not discontinued)
    const newStatus = data.discontinued ? 'discontinued' : 'completed';
    try {
      await addressService.updateStatus(
        data.workloadId,
        data.addressId,
        newStatus,
        userId,
        userName
      );
    } catch (error) {
      console.error('Failed to update address status:', error);
      // Don't fail the response creation if status update fails
    }

    // Update workload progress counts
    try {
      await workloadService.incrementSurveyResponse(
        data.workloadId,
        !data.discontinued
      );
    } catch (error) {
      console.error('Failed to update workload progress:', error);
      // Don't fail the response creation if progress update fails
    }
  }

  return response;
}

export async function saveDraft(
  data: Partial<CreateSurveyResponseDto>,
  userId: string,
  userName: string
): Promise<SurveyResponse> {
  const now = Timestamp.now();

  const responseData = {
    surveyId: data.surveyId,
    surveyVersion: data.surveyVersion,
    workloadId: data.workloadId || null,
    addressId: data.addressId || null,
    projectId: data.projectId,
    respondentId: userId,
    respondentName: userName,
    responses: data.responses || [],
    status: 'draft' as const,
    startedAt: data.startedAt || now,
    completedAt: null,
    duration: null,
    location: null,
    deviceInfo: null,
    syncStatus: 'synced' as const,
    createdAt: now,
    updatedAt: now,
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), responseData);

  return {
    id: docRef.id,
    ...responseData,
  } as SurveyResponse;
}


export async function getResponse(responseId: string): Promise<SurveyResponse | null> {
  const docRef = doc(db, COLLECTION_NAME, responseId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as SurveyResponse;
}

export async function getByProject(
  projectId: string,
  maxResults: number = 500,
  includeDiscontinued: boolean = true
): Promise<SurveyResponse[]> {
  // Query without status filter to get all responses including discontinued
  const q = query(
    collection(db, COLLECTION_NAME),
    where('projectId', '==', projectId),
    orderBy('completedAt', 'desc'),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  let responses = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as SurveyResponse[];

  // Filter out drafts, but include completed and discontinued
  responses = responses.filter(r => r.status !== 'draft' && r.status !== 'in_progress');

  // Optionally filter out discontinued
  if (!includeDiscontinued) {
    responses = responses.filter(r => !r.discontinued && r.status !== 'discontinued');
  }

  return responses;
}

export async function getSurveyResponses(
  surveyId: string,
  maxResults: number = 100
): Promise<SurveyResponse[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('surveyId', '==', surveyId),
    where('status', '==', 'completed'),
    orderBy('completedAt', 'desc'),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as SurveyResponse[];
}

export async function getWorkloadResponses(workloadId: string): Promise<SurveyResponse[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('workloadId', '==', workloadId),
    orderBy('completedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as SurveyResponse[];
}

export async function getAddressResponse(
  workloadId: string,
  addressId: string
): Promise<SurveyResponse | null> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('workloadId', '==', workloadId),
    where('addressId', '==', addressId),
    where('status', '==', 'completed'),
    limit(1)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }

  return {
    id: snapshot.docs[0].id,
    ...snapshot.docs[0].data(),
  } as SurveyResponse;
}

export async function getUserResponses(
  userId: string,
  projectId?: string
): Promise<SurveyResponse[]> {
  const constraints = [
    where('respondentId', '==', userId),
    orderBy('completedAt', 'desc'),
  ];

  if (projectId) {
    constraints.push(where('projectId', '==', projectId));
  }

  const q = query(collection(db, COLLECTION_NAME), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as SurveyResponse[];
}

export async function getUserDrafts(userId: string): Promise<SurveyResponse[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('respondentId', '==', userId),
    where('status', '==', 'draft'),
    orderBy('updatedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as SurveyResponse[];
}


export async function updateDraft(
  responseId: string,
  responses: QuestionResponse[]
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, responseId);

  await updateDoc(docRef, {
    responses,
    updatedAt: Timestamp.now(),
  });
}

export async function completeDraft(
  responseId: string,
  responses: QuestionResponse[],
  location?: LocationCapture
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, responseId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Response not found');
  }

  const now = Timestamp.now();
  const startedAt = existing.data().startedAt;

  await updateDoc(docRef, {
    responses,
    status: 'completed',
    completedAt: now,
    duration: startedAt ? now.toMillis() - startedAt.toMillis() : null,
    location: location
      ? {
          lat: location.lat,
          lng: location.lng,
          accuracy: location.accuracy,
          capturedAt: Timestamp.fromMillis(location.timestamp),
        }
      : null,
    updatedAt: now,
  });
}


export async function getSurveyStats(surveyId: string): Promise<{
  totalResponses: number;
  completedResponses: number;
  draftResponses: number;
  averageDuration: number;
  responsesByDay: Record<string, number>;
  questionStats: Record<string, { answered: number; skipped: number }>;
}> {
  const responses = await getSurveyResponses(surveyId, 1000);

  const stats = {
    totalResponses: responses.length,
    completedResponses: responses.filter((r) => r.status === 'completed').length,
    draftResponses: responses.filter((r) => r.status === 'draft').length,
    averageDuration: 0,
    responsesByDay: {} as Record<string, number>,
    questionStats: {} as Record<string, { answered: number; skipped: number }>,
  };

  // Calculate average duration
  const durationsMs = responses
    .filter((r) => r.duration)
    .map((r) => r.duration!);
  if (durationsMs.length > 0) {
    stats.averageDuration = durationsMs.reduce((a, b) => a + b, 0) / durationsMs.length / 1000; // Convert to seconds
  }

  // Group by day
  responses.forEach((response) => {
    if (response.completedAt) {
      const day = response.completedAt.toDate().toISOString().split('T')[0];
      stats.responsesByDay[day] = (stats.responsesByDay[day] || 0) + 1;
    }

    // Count question responses
    response.responses.forEach((qr) => {
      if (!stats.questionStats[qr.questionId]) {
        stats.questionStats[qr.questionId] = { answered: 0, skipped: 0 };
      }
      if (qr.skipped) {
        stats.questionStats[qr.questionId].skipped++;
      } else {
        stats.questionStats[qr.questionId].answered++;
      }
    });
  });

  return stats;
}

export async function getResponseDistribution(
  surveyId: string,
  questionId: string
): Promise<Record<string, number>> {
  const responses = await getSurveyResponses(surveyId, 1000);
  const distribution: Record<string, number> = {};

  responses.forEach((response) => {
    const questionResponse = response.responses.find(
      (r) => r.questionId === questionId
    );
    if (questionResponse && !questionResponse.skipped) {
      const value = Array.isArray(questionResponse.value)
        ? questionResponse.value.join(', ')
        : String(questionResponse.value);
      distribution[value] = (distribution[value] || 0) + 1;
    }
  });

  return distribution;
}


export function subscribeToSurveyResponses(
  surveyId: string,
  callback: (responses: SurveyResponse[]) => void
): () => void {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('surveyId', '==', surveyId),
    where('status', '==', 'completed'),
    orderBy('completedAt', 'desc'),
    limit(100)
  );

  return onSnapshot(q, (snapshot) => {
    const responses = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as SurveyResponse[];
    callback(responses);
  });
}

// Export service object
export const surveyResponseService = {
  create: createResponse,
  saveDraft,
  get: getResponse,
  getByProject,
  getSurveyResponses,
  getWorkloadResponses,
  getAddressResponse,
  getUserResponses,
  getUserDrafts,
  updateDraft,
  completeDraft,
  getStats: getSurveyStats,
  getDistribution: getResponseDistribution,
  subscribeToSurveyResponses,
};

export default surveyResponseService;
